import * as React from 'react';
import cx from 'classnames';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DraggableListDataItem, DraggableListDirection } from './draggable-list.types';
import { Item } from './components/item';

import './draggable-list.styles.scss';

export type DraggableListProps<T extends DraggableListDataItem = DraggableListDataItem> = {
  classes?: {
    item?: string | ((item: T) => string);
  };
  style?: React.CSSProperties;
  className?: string;
  direction?: DraggableListDirection;
  items: Array<T>;
  renderItem: (data: T, index: number) => React.ReactNode;
  onChange: (list: Array<T>) => void;
};

export function DraggableList<T extends DraggableListDataItem>(props: DraggableListProps<T>) {
  const {
    classes,
    style,
    className,
    direction = DraggableListDirection.vertical,
    items,
    renderItem,
    onChange,
  } = props;

  const [isDragging, setDragging] = React.useState(false);

  const moveCard = React.useCallback((dragFromIndex: number, currentDropIndex: number) => {
    const movingItem = items[dragFromIndex];
    const itemToReplace = items[currentDropIndex];

    const updatedList = items.map((item, i) => {
      if (i === dragFromIndex && itemToReplace) {
        return itemToReplace;
      }

      if (i === currentDropIndex && movingItem) {
        return movingItem;
      }

      return item;
    });

    onChange(updatedList);
  }, [items, onChange]);

  const renderListItem = React.useCallback((item: T, index: number) => {
    return (
      <Item
        className={typeof classes?.item === 'function' ? classes.item(item) : classes?.item}
        direction={direction}
        index={index}
        data={item}
        renderContent={renderItem}
        moveCard={moveCard}
        onDraggingStatusChange={setDragging}
      />
    )
  }, [renderItem, moveCard, direction, classes?.item]);


  return (
    <DndProvider backend={HTML5Backend}>
      <div
        style={style}
        className={cx(
          'DraggableList',
          {
            ['DraggableList--horizontal']: direction === DraggableListDirection.horizontal,
            ['DraggableList--active']: isDragging,
          },
          className
        )}
      >
        {items.map((item, i) => <React.Fragment key={item.id}>{renderListItem(item, i)}</React.Fragment>)}
      </div>
    </DndProvider>
  );
};


